/**
 * Scanner caméra (QR + codes-barres) réutilisable.
 *
 * Extrait de pages/panier/index.vue (PWA client), qui reste inchangée : elle est
 * en production, elle fonctionne, et la refactoriser ne servirait que l'élégance.
 * Cette classe sert la caisse (modal webcam) et le téléphone-scanner.
 *
 * Stratégie de décodage, dans cet ordre :
 *  1. BarcodeDetector natif - présent sur Android Chrome, décodage matériel, très
 *     supérieur en vitesse et en tolérance au flou.
 *  2. @zxing/browser + @zxing/library en IMPORT DYNAMIQUE - repli, et hors du
 *     bundle principal tant que personne ne scanne.
 *
 * ⚠️ getUserMedia exige un contexte sécurisé : HTTPS ou localhost. Sur une IP LAN
 * en HTTP, demarrer() échouera - c'est une limite du navigateur, pas un bug.
 */

/** Formats par défaut : QR seul. La caisse et le téléphone élargissent au besoin. */
const FORMATS_DEFAUT = ["qr_code"];

/** Intervalle de la boucle du détecteur natif (ms). */
const PERIODE_DETECTION = 110;

/** Correspondance des noms de format : API native → ZXing. */
const FORMATS_ZXING = {
    qr_code: "QR_CODE",
    ean_13: "EAN_13",
    ean_8: "EAN_8",
    upc_a: "UPC_A",
    upc_e: "UPC_E",
    code_128: "CODE_128",
    code_39: "CODE_39",
    itf: "ITF",
};

export class QrScanner {
    constructor() {
        this.stream = null;
        this.track = null;
        this.reader = null;
        this.detector = null;
        this.controls = null;
        this.timerId = null;
        this.actif = false;
        this.torche = false;
        this.torcheDispo = false;
        this.onDecoded = null;
    }

    /**
     * @param {HTMLVideoElement} video élément déjà monté dans le DOM
     * @param {{formats?: string[], facingMode?: string, onDecoded: (txt: string) => void}} opts
     * @returns {Promise<boolean>} false si la caméra n'a pas pu démarrer
     */
    async demarrer(video, opts = {}) {
        if (this.actif) return true;
        if (!video) return false;

        const formats = opts.formats || FORMATS_DEFAUT;
        const facingMode = opts.facingMode || "environment";
        this.onDecoded = opts.onDecoded || null;

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    /* 'ideal' et jamais 'exact' : un poste sans caméra arrière
                       doit se rabattre sur ce qu'il a, pas lever OverconstrainedError. */
                    facingMode: { ideal: facingMode },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
            });
            video.srcObject = this.stream;
            await video.play();

            this.track = this.stream.getVideoTracks()[0];
            const caps = this.track.getCapabilities ? this.track.getCapabilities() : {};
            this.torcheDispo = !!caps.torch;
            try {
                if (caps.focusMode && caps.focusMode.indexOf("continuous") !== -1) {
                    await this.track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
                }
            } catch (e) { /* ignore */ }

            this.actif = true;

            if ("BarcodeDetector" in window) {
                await this._demarrerNatif(video, formats);
            } else {
                await this._demarrerZxing(video, formats);
            }
            return true;
        } catch (e) {
            console.warn("[qrscanner] démarrage impossible:", e);
            this.arreter();
            return false;
        }
    }

    async _demarrerNatif(video, formats) {
        try {
            let dispo = formats;
            if (window.BarcodeDetector.getSupportedFormats) {
                const supportes = await window.BarcodeDetector.getSupportedFormats();
                dispo = formats.filter((f) => supportes.indexOf(f) !== -1);
            }
            /* Le navigateur annonce BarcodeDetector mais pas nos formats : ZXing
               les gère, lui. Sans ce repli on aurait une caméra qui ne décode rien. */
            if (dispo.length === 0) { await this._demarrerZxing(video, formats); return; }

            this.detector = new window.BarcodeDetector({ formats: dispo });
            const boucle = async () => {
                if (!this.actif) return;
                try {
                    const codes = await this.detector.detect(video);
                    if (codes && codes.length) this._emettre(codes[0].rawValue);
                } catch (e) { /* frame sans code */ }
                this.timerId = setTimeout(boucle, PERIODE_DETECTION);
            };
            boucle();
        } catch (e) {
            console.warn("[qrscanner] BarcodeDetector KO, bascule ZXing:", e);
            await this._demarrerZxing(video, formats);
        }
    }

    async _demarrerZxing(video, formats) {
        const browserMod = await import("@zxing/browser");
        const libMod = await import("@zxing/library");
        const { BrowserMultiFormatReader } = browserMod;
        const { DecodeHintType, BarcodeFormat } = libMod;

        const possibles = formats
            .map((f) => BarcodeFormat[FORMATS_ZXING[f]])
            .filter((f) => f !== undefined);

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, possibles);
        hints.set(DecodeHintType.TRY_HARDER, true);

        this.reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });
        /* decodeFromStream et non decodeFromVideoDevice : le flux est déjà ouvert,
           en rouvrir un second bloquerait la caméra sur certains appareils. */
        this.controls = await this.reader.decodeFromStream(
            this.stream, video,
            (result) => { if (result) this._emettre(result.getText()); }
        );
    }

    _emettre(txt) {
        if (!this.actif || !this.onDecoded) return;
        this.onDecoded(txt);
    }

    async basculerTorche() {
        if (!this.track) return false;
        try {
            this.torche = !this.torche;
            await this.track.applyConstraints({ advanced: [{ torch: this.torche }] });
            return this.torche;
        } catch (e) {
            this.torche = false;
            return false;
        }
    }

    torcheDisponible() { return this.torcheDispo; }

    /** Idempotent : appelable plusieurs fois, et depuis un beforeUnmount. */
    arreter() {
        this.actif = false;
        if (this.timerId) { clearTimeout(this.timerId); this.timerId = null; }
        try { if (this.controls) this.controls.stop(); } catch (e) { /* ignore */ }
        this.controls = null;
        this.reader = null;
        this.detector = null;
        /* Le point critique : sans ce stop() sur chaque track, la LED de la caméra
           reste allumée et le périphérique reste verrouillé. */
        try { if (this.stream) this.stream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
        this.stream = null;
        this.track = null;
        this.torche = false;
        this.torcheDispo = false;
        this.onDecoded = null;
    }
}
